import { useEffect, useMemo, useState } from "react";

import { DateRange } from "react-day-picker";

import dayjs from "dayjs";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Card, CardContent } from "~/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { TableSkeleton } from "~/components/ui/skeletons";
import { Tabs, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { supabase } from "~/integrations/supabase/client";
import { BulkActionToolbar } from "~/pages/operations/components/BulkActionToolbar";
import { BulkTransportistaAssignmentDialog } from "~/pages/operations/components/BulkTransportistaAssignmentDialog";
import DeliveredDateDialog from "~/pages/operations/components/DeliveredDateDialog";
import { OperationsOrdersFilterBar } from "~/pages/operations/components/OperationsOrdersFilterBar";
import { OperationsOrdersPagination } from "~/pages/operations/components/OperationsOrdersPagination";
import {
  OperationsOrdersTable,
  SortConfig,
  SortField,
} from "~/pages/operations/components/OperationsOrdersTable";
import {
  AcceptedActions,
  DeliveredActions,
  FacturadoActions,
  PendingActions,
  PorFacturarActions,
  TransitActions,
} from "~/pages/operations/components/OrderActionButtons";
import { OrderStatusBadge } from "~/pages/operations/components/OrderStatusBadge";
import { useDispatchGuide } from "~/pages/operations/hooks/useDispatchGuide";
import { useFacturaNumbers } from "~/pages/operations/hooks/useFacturaNumbers";
import { useOrderSelection } from "~/pages/operations/hooks/useOrderSelection";
import { useOrderStatusChange } from "~/pages/operations/hooks/useOrderStatusChange";
import { CreateDispatchGuideDialog } from "~/pages/purchase-orders/components/order-detail/CreateDispatchGuideDialog";
import { useSalesRequests } from "~/pages/purchase-orders/hooks/useSalesRequests";
import OrderDetailDialog from "~/pages/purchase-orders/OrderDetailDialog";
import { bulkPrintFacturas } from "~/services/documentDownloadService";
import { SalesRequest } from "~/types/client";
import { TransportData } from "~/types/dispatch";

// UUID pattern for detecting order ID searches
const PARTIAL_UUID_PATTERN = /^[0-9a-f]{8,}$/i;
// Numeric pattern for detecting factura number searches
const NUMERIC_PATTERN = /^\d{4,}$/;

const OperationsOrdersPage = () => {
  // State for search and tab selection
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSeller, setSelectedSeller] = useState("all");
  const [subTab, setSubTab] = useState("pending"); // Default to "Pendientes"
  const [selectedOrder, setSelectedOrder] = useState<SalesRequest | null>(null); // State for dialog
  const [transportistaDialogOpen, setTransportistaDialogOpen] = useState(false);

  // State for sorting
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: null,
    direction: "asc",
  });

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // State for filters
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [selectedTransportista, setSelectedTransportista] = useState("all");
  const [selectedGroup, setSelectedGroup] = useState("all");

  // Confirmation dialog for the bulk "Marcar como Entregado" action
  const [markDeliveredConfirmOpen, setMarkDeliveredConfirmOpen] =
    useState(false);

  // State for server-side order ID search results
  const [directSearchResults, setDirectSearchResults] = useState<
    SalesRequest[]
  >([]);
  // State for client names from server-side search results
  const [directSearchClientNames, setDirectSearchClientNames] = useState<
    Record<string, string>
  >({});
  // State for print loading
  const [isPrinting, setIsPrinting] = useState(false);

  // Fetch data from Supabase
  const {
    salesRequests,
    salesRequestItems,
    clientNames,
    clientInfo,
    clientRiskStatus,
    isLoading,
    getSellerInfo,
    getApproverRejecterInfo,
    fetchSalesRequests,
  } = useSalesRequests();

  // Get factura numbers and emission dates for search and filtering
  const { getFacturaNumber, facturaEmissionDates } =
    useFacturaNumbers(salesRequests);

  // Server-side search for order ID (UUID) or factura number
  useEffect(() => {
    const searchServerSide = async () => {
      const searchLower = searchQuery.toLowerCase().trim();
      const isUuidSearch =
        searchLower.length >= 8 && PARTIAL_UUID_PATTERN.test(searchLower);
      const isFacturaSearch = NUMERIC_PATTERN.test(searchLower);

      // Only search if it looks like a UUID or factura number
      if (!isUuidSearch && !isFacturaSearch) {
        setDirectSearchResults([]);
        setDirectSearchClientNames({});
        return;
      }

      try {
        let salesRequestIds: string[] = [];

        // Search by UUID pattern
        if (isUuidSearch) {
          const { data, error } = await supabase
            .from("sales_request")
            .select("id")
            .ilike("id", `%${searchLower}%`)
            .is("deleted_at", null)
            .limit(10);

          if (!error && data) {
            salesRequestIds = data.map((r) => r.id);
          }
        }

        // Search by factura number (bsale_number in sales_request_documents)
        if (isFacturaSearch) {
          const { data, error } = await supabase
            .from("sales_request_documents")
            .select("sales_request_id")
            .eq("document_type", "factura")
            .ilike("bsale_number", `%${searchLower}%`)
            .limit(10);

          if (!error && data) {
            const facturaIds = data.map((d) => d.sales_request_id);
            // Merge with UUID results, avoiding duplicates
            salesRequestIds = [...new Set([...salesRequestIds, ...facturaIds])];
          }
        }

        if (salesRequestIds.length === 0) {
          setDirectSearchResults([]);
          setDirectSearchClientNames({});
          return;
        }

        // Fetch full sales request data for found IDs
        const { data, error } = await supabase
          .from("sales_request")
          .select("*")
          .in("id", salesRequestIds)
          .is("deleted_at", null);

        if (error) {
          console.error("Error fetching sales requests:", error);
          setDirectSearchResults([]);
          return;
        }

        if (data && data.length > 0) {
          // Fetch client names for the found orders
          const clientIds = [...new Set(data.map((req) => req.client_id))];
          const { data: clientsData } = await supabase
            .from("clients")
            .select("id, name")
            .in("id", clientIds);

          if (clientsData) {
            const clientNameMap: Record<string, string> = {};
            clientsData.forEach((client) => {
              clientNameMap[client.id] = client.name || "Cliente sin nombre";
            });
            setDirectSearchClientNames(clientNameMap);
          }

          // Format to SalesRequest type
          const formattedResults: SalesRequest[] = data.map((req) => ({
            id: req.id,
            clientId: req.client_id,
            createdBy: req.created_by,
            approvedRejectedBy: req.approved_rejected_by || undefined,
            status: req.status,
            paid: req.paid,
            paid_amount: req.paid_amount || 0,
            paymentTerms: req.payment_terms,
            paymentType: req.payment_type,
            paymentDueDate: req.payment_due_date,
            totalAmount: req.total_amount,
            hasPricingWarning: req.has_pricing_warning || false,
            comments: req.comments || undefined,
            internal_notes: req.internal_notes || undefined,
            factura_observations: req.factura_observations || undefined,
            deliveryDate: req.delivery_date || undefined,
            deliveryAddressId: req.delivery_address_id || undefined,
            deliveryStreet: req.delivery_street || undefined,
            deliveryNumber: req.delivery_number || undefined,
            deliveryComplement: req.delivery_complement || undefined,
            deliveryCity: req.delivery_city || undefined,
            deliveryState: req.delivery_state || undefined,
            deliveryComuna: req.delivery_comuna || undefined,
            rejectedReason: req.rejected_reason || undefined,
            createdAt: req.created_at,
            updatedAt: req.updated_at,
            salesConditionId: req.sales_condition_id || undefined,
            assigned_transportista: req.assigned_transportista || undefined,
          }));
          setDirectSearchResults(formattedResults);
        } else {
          setDirectSearchResults([]);
          setDirectSearchClientNames({});
        }
      } catch (error) {
        console.error("Error in server-side search:", error);
        setDirectSearchResults([]);
      }
    };

    // Debounce the search
    const timeoutId = setTimeout(searchServerSide, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Use the custom hook for order selection (must be before useEffect that uses clearSelection)
  const {
    selectedOrderIds,
    handleSelectOrder,
    handleSelectAll,
    clearSelection,
    getSelectedOrders,
    isAllSelected,
  } = useOrderSelection();

  // Clear selection and reset pagination when changing tabs
  useEffect(() => {
    clearSelection();
    setCurrentPage(1);
    // Clear transportista filter when switching to non-applicable tabs
    if (!["in_transit", "delivered"].includes(subTab)) {
      setSelectedTransportista("all");
    }
    // Clear date filter when switching to non-applicable tabs
    if (!["facturado", "in_transit", "delivered"].includes(subTab)) {
      setDateRange(undefined);
    }
  }, [subTab, clearSelection]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchQuery,
    selectedSeller,
    selectedGroup,
    dateRange,
    selectedTransportista,
    sortConfig,
  ]);

  // Check URL parameters on component mount to auto-open order detail
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const orderIdToOpen = urlParams.get("open");
    const guiaGenerated = urlParams.get("guia_generated");

    if (
      orderIdToOpen &&
      guiaGenerated === "true" &&
      salesRequests.length > 0 &&
      !isLoading
    ) {
      // Find the order in the sales requests
      const orderToOpen = salesRequests.find(
        (order) => order.id === orderIdToOpen
      );
      if (orderToOpen) {
        setSelectedOrder(orderToOpen);
      }

      // Clean up URL parameters
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete("open");
      newUrl.searchParams.delete("guia_generated");
      window.history.replaceState({}, "", newUrl.toString());
    }
  }, [salesRequests, isLoading]);

  // Use the custom hook for order status changes
  const {
    isLoadingTransition,
    isBulkProcessing,
    deliveryDialogOpen,
    setDeliveryDialogOpen,
    setOrderInTransit,
    openDeliveryDialog,
    confirmDelivery,
    setBulkOrdersInTransit,
    openBulkDeliveryDialog,
    markOrderDelivered,
    markBulkOrdersDelivered,
  } = useOrderStatusChange(fetchSalesRequests);

  const { isCreatingDispatchGuide, createDispatchGuide } =
    useDispatchGuide(fetchSalesRequests);

  // Order whose guía-creation dialog (transport data) is open.
  const [guiaDialogOrder, setGuiaDialogOrder] = useState<SalesRequest | null>(
    null
  );

  const handleConfirmGuia = async (
    transport: TransportData,
    observations: string
  ) => {
    if (!guiaDialogOrder) return;
    try {
      await createDispatchGuide(guiaDialogOrder, transport, observations);
      setGuiaDialogOrder(null);
    } catch (error) {
      console.error("Failed to create dispatch guide:", error);
    }
  };

  // Helper function to get client name
  const getClientName = (clientId: string) => {
    return (
      clientNames[clientId] ||
      directSearchClientNames[clientId] ||
      "Cliente desconocido"
    );
  };

  // Helper function to get client RUT
  const getClientRut = (clientId: string) => {
    return clientInfo[clientId]?.taxId || "";
  };

  // Generate list of unique sellers
  const sellers = Array.from(
    new Set(salesRequests.map((order) => order.createdBy))
  ).map((sellerId) => {
    const sellerInfo = getSellerInfo(sellerId);
    return {
      id: sellerId,
      name: sellerInfo.name,
      lastname: sellerInfo.lastname,
    };
  });

  // Map order id → set of product groups (products.group_description) from its
  // line items, for the "Grupo" filter.
  const orderGroups = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    Object.entries(salesRequestItems).forEach(([srId, items]) => {
      const groups = new Set<string>();
      items.forEach((item) => {
        const g = item.group?.trim();
        if (g) groups.add(g);
      });
      map[srId] = groups;
    });
    return map;
  }, [salesRequestItems]);

  // Distinct product groups present across loaded orders (for the "Grupo" filter).
  const groupOptions = useMemo(() => {
    const set = new Set<string>();
    Object.values(orderGroups).forEach((groups) => {
      groups.forEach((g) => set.add(g));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [orderGroups]);

  // Filter orders by status, search query, seller, and group
  // Merge with direct search results from server-side order ID search
  const filteredOrders = useMemo(() => {
    const searchLower = searchQuery.toLowerCase();

    // Filter loaded sales requests
    const loadedFiltered = salesRequests.filter((order) => {
      const clientName = getClientName(order.clientId).toLowerCase();
      const clientRut = getClientRut(order.clientId).toLowerCase();
      const facturaNumber = getFacturaNumber(order.id).toLowerCase();
      const orderId = order.id.toString().toLowerCase();

      // Check search query
      const matchesSearch =
        clientName.includes(searchLower) ||
        clientRut.includes(searchLower) ||
        facturaNumber.includes(searchLower) ||
        orderId.includes(searchLower);

      // Check seller filter
      const matchesSeller =
        selectedSeller === "all" || order.createdBy === selectedSeller;

      // Check group filter (order has ≥1 line item in the selected group)
      const matchesGroup =
        selectedGroup === "all" ||
        (orderGroups[order.id]?.has(selectedGroup) ?? false);

      return matchesSearch && matchesSeller && matchesGroup;
    });

    // Merge with direct search results (from server-side order ID search)
    // Add any results that aren't already in the loaded list
    const loadedIds = new Set(loadedFiltered.map((o) => o.id));
    const additionalResults = directSearchResults.filter((order) => {
      // Not already in loaded results
      if (loadedIds.has(order.id)) return false;

      // Check seller filter
      const matchesSeller =
        selectedSeller === "all" || order.createdBy === selectedSeller;

      // Check group filter
      const matchesGroup =
        selectedGroup === "all" ||
        (orderGroups[order.id]?.has(selectedGroup) ?? false);

      return matchesSeller && matchesGroup;
    });

    return [...additionalResults, ...loadedFiltered];
  }, [
    salesRequests,
    directSearchResults,
    searchQuery,
    selectedSeller,
    selectedGroup,
    orderGroups,
    clientNames,
    clientInfo,
  ]);

  // Show pendientes, aceptados, por facturar, en tránsito, entregados
  const pendingOrders = filteredOrders.filter(
    (order) => order.status === "pending"
  );
  const acceptedOrders = filteredOrders.filter(
    (order) => order.status === "accepted"
  );
  const porFacturarOrders = filteredOrders.filter(
    (order) => order.status === "por_facturar"
  );
  // Facturado: in_transit WITHOUT assigned transportista
  const facturadoOrders = filteredOrders.filter(
    (order) => order.status === "in_transit" && !order.assigned_transportista
  );
  // En Tránsito: in_transit WITH assigned transportista
  const inTransitOrders = filteredOrders.filter(
    (order) => order.status === "in_transit" && order.assigned_transportista
  );
  const deliveredOrders = filteredOrders.filter(
    (order) => order.status === "delivered"
  );

  // Button handler for the dialog
  const handleRowDetailClick = (order: SalesRequest) => {
    setSelectedOrder(order);
  };

  // Get base orders for the active tab
  const baseOrders = useMemo(() => {
    switch (subTab) {
      case "pending":
        return pendingOrders;
      case "accepted":
        return acceptedOrders;
      case "por_facturar":
        return porFacturarOrders;
      case "facturado":
        return facturadoOrders;
      case "in_transit":
        return inTransitOrders;
      case "delivered":
        return deliveredOrders;
      default:
        return [];
    }
  }, [
    subTab,
    pendingOrders,
    acceptedOrders,
    porFacturarOrders,
    facturadoOrders,
    inTransitOrders,
    deliveredOrders,
  ]);

  // Apply date range filter (for facturado, in_transit, delivered tabs)
  const dateFilteredOrders = useMemo(() => {
    if (
      !dateRange?.from ||
      !["facturado", "in_transit", "delivered"].includes(subTab)
    ) {
      return baseOrders;
    }

    return baseOrders.filter((order) => {
      const emissionDate = facturaEmissionDates[order.id];
      if (!emissionDate) return false;

      const date = dayjs.unix(emissionDate);
      const fromDate = dayjs(dateRange.from).startOf("day");
      const toDate = dateRange.to
        ? dayjs(dateRange.to).endOf("day")
        : dayjs().endOf("day");

      return (
        (date.isAfter(fromDate) || date.isSame(fromDate)) &&
        (date.isBefore(toDate) || date.isSame(toDate))
      );
    });
  }, [baseOrders, dateRange, facturaEmissionDates, subTab]);

  // Apply transportista filter (for in_transit, delivered tabs)
  const transportistaFilteredOrders = useMemo(() => {
    if (
      selectedTransportista === "all" ||
      !["in_transit", "delivered"].includes(subTab)
    ) {
      return dateFilteredOrders;
    }

    return dateFilteredOrders.filter(
      (order) => order.assigned_transportista === selectedTransportista
    );
  }, [dateFilteredOrders, selectedTransportista, subTab]);

  // Apply sorting
  const sortedOrders = useMemo(() => {
    if (!sortConfig.field) return transportistaFilteredOrders;

    return [...transportistaFilteredOrders].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortConfig.field) {
        case "facturaNumber":
          aValue = getFacturaNumber(a.id);
          bValue = getFacturaNumber(b.id);
          // Put "Sin factura" at the end
          if (aValue === "Sin factura" && bValue !== "Sin factura") return 1;
          if (bValue === "Sin factura" && aValue !== "Sin factura") return -1;
          break;
        case "clientName":
          aValue = getClientName(a.clientId).toLowerCase();
          bValue = getClientName(b.clientId).toLowerCase();
          break;
        case "sellerName":
          aValue =
            `${getSellerInfo(a.createdBy).name} ${getSellerInfo(a.createdBy).lastname}`.toLowerCase();
          bValue =
            `${getSellerInfo(b.createdBy).name} ${getSellerInfo(b.createdBy).lastname}`.toLowerCase();
          break;
        case "createdAt":
          aValue = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bValue = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          break;
        case "deliveryStreet":
          aValue = (a.deliveryStreet || "").toLowerCase();
          bValue = (b.deliveryStreet || "").toLowerCase();
          break;
        case "totalAmount":
          aValue = a.totalAmount || 0;
          bValue = b.totalAmount || 0;
          break;
        default:
          return 0;
      }

      // Handle string comparison
      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortConfig.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Handle numeric comparison
      if (sortConfig.direction === "asc") {
        return (aValue as number) - (bValue as number);
      } else {
        return (bValue as number) - (aValue as number);
      }
    });
  }, [
    transportistaFilteredOrders,
    sortConfig,
    getFacturaNumber,
    getClientName,
    getSellerInfo,
  ]);

  // Apply pagination
  const { paginatedOrders, totalPages, startIndex, endIndex } = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return {
      paginatedOrders: sortedOrders.slice(start, end),
      totalPages: Math.ceil(sortedOrders.length / itemsPerPage),
      startIndex: Math.min(start + 1, sortedOrders.length),
      endIndex: Math.min(end, sortedOrders.length),
    };
  }, [sortedOrders, currentPage, itemsPerPage]);

  // Handler for sorting
  const handleSort = (field: SortField) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Handler for clearing filters
  const handleClearFilters = () => {
    setDateRange(undefined);
    setSelectedTransportista("all");
    setSelectedGroup("all");
  };

  // Current orders is the paginated result
  const currentOrders = paginatedOrders;

  // Bulk action handlers
  const handleBulkInTransit = async () => {
    const selected = getSelectedOrders(sortedOrders);
    await setBulkOrdersInTransit(selected);
    clearSelection();
  };

  const handleBulkDelivery = () => {
    const selected = getSelectedOrders(sortedOrders);
    openBulkDeliveryDialog(selected);
  };

  const handleBulkMarkDelivered = () => {
    setMarkDeliveredConfirmOpen(true);
  };

  const confirmBulkMarkDelivered = async () => {
    const selected = getSelectedOrders(sortedOrders);
    setMarkDeliveredConfirmOpen(false);
    await markBulkOrdersDelivered(selected);
    clearSelection();
  };

  const handleBulkAssignTransportista = () => {
    setTransportistaDialogOpen(true);
  };

  const handleBulkPrint = async () => {
    if (selectedOrderIds.size === 0) return;
    setIsPrinting(true);
    try {
      const ids = Array.from(selectedOrderIds);
      await bulkPrintFacturas(ids);
      clearSelection();
    } finally {
      setIsPrinting(false);
    }
  };

  const tabs = [
    {
      key: "pending",
      label: "Pendientes",
      orders: pendingOrders,
      actions: (order: SalesRequest) => (
        <PendingActions
          order={order}
          onCreateDispatchGuide={(order) => setGuiaDialogOrder(order)}
          isCreatingDispatchGuide={isCreatingDispatchGuide}
        />
      ),
      statusBadge: <OrderStatusBadge status="pending" />,
      showActions: true,
    },
    {
      key: "accepted",
      label: "Aceptados",
      orders: acceptedOrders,
      actions: (order: SalesRequest) => (
        <AcceptedActions
          order={order}
          isLoadingTransition={isLoadingTransition}
          onInTransit={setOrderInTransit}
          onCreateDispatchGuide={(order) => setGuiaDialogOrder(order)}
          isCreatingDispatchGuide={isCreatingDispatchGuide}
        />
      ),
      statusBadge: <OrderStatusBadge status="accepted" />,
      showActions: true,
    },
    {
      key: "por_facturar",
      label: "Por Facturar",
      orders: porFacturarOrders,
      actions: (order: SalesRequest) => (
        <PorFacturarActions
          order={order}
          onCreateDispatchGuide={(order) => setGuiaDialogOrder(order)}
          isCreatingDispatchGuide={isCreatingDispatchGuide}
        />
      ),
      statusBadge: <OrderStatusBadge status="por_facturar" />,
      showActions: true,
    },
    {
      key: "facturado",
      label: "Facturado",
      orders: facturadoOrders,
      actions: (order: SalesRequest) => (
        <FacturadoActions
          order={order}
          isLoadingTransition={isLoadingTransition}
          onMarkDelivered={markOrderDelivered}
        />
      ),
      statusBadge: <OrderStatusBadge status="facturado" />,
      showActions: true,
    },
    {
      key: "in_transit",
      label: "En tránsito",
      orders: inTransitOrders,
      actions: (order: SalesRequest) => (
        <TransitActions
          order={order}
          isLoadingTransition={isLoadingTransition}
          onDelivery={openDeliveryDialog}
        />
      ),
      statusBadge: <OrderStatusBadge status="in_transit" />,
      showActions: true,
    },
    {
      key: "delivered",
      label: "Entregados",
      orders: deliveredOrders,
      actions: () => <DeliveredActions />,
      statusBadge: <OrderStatusBadge status="delivered" />,
      showActions: false,
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Envíos"
        description="Gestiona los pedidos por estado: despacho, facturación y entrega."
      />

      {/* Status tabs */}
      <Tabs value={subTab} onValueChange={setSubTab} className="w-full">
        {/* Mobile: dropdown selector */}
        <div className="sm:hidden">
          <Select value={subTab} onValueChange={setSubTab}>
            <SelectTrigger className="h-9 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {tabs.map((tab) => (
                <SelectItem key={tab.key} value={tab.key}>
                  {tab.label} ({tab.orders.length})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {/* Desktop: tabs */}
        <TabsList className="hidden h-auto w-full justify-start p-1 sm:flex">
          {tabs.map((tab) => (
            <TabsTrigger
              key={tab.key}
              value={tab.key}
              className="shrink-0 gap-2"
            >
              {tab.label}
              <Badge
                variant={subTab === tab.key ? "default" : "secondary"}
                className="px-1.5 py-0"
              >
                {tab.orders.length}
              </Badge>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="p-4">
          <OperationsOrdersFilterBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            selectedSeller={selectedSeller}
            setSelectedSeller={setSelectedSeller}
            sellers={sellers}
            selectedGroup={selectedGroup}
            setSelectedGroup={setSelectedGroup}
            groups={groupOptions}
            currentTab={subTab}
            dateRange={dateRange}
            setDateRange={setDateRange}
            selectedTransportista={selectedTransportista}
            setSelectedTransportista={setSelectedTransportista}
            onClearFilters={handleClearFilters}
          />
        </CardContent>
      </Card>

      <BulkActionToolbar
        selectedCount={selectedOrderIds.size}
        currentTab={subTab}
        onClearSelection={clearSelection}
        onBulkInTransit={handleBulkInTransit}
        onBulkDelivery={handleBulkDelivery}
        onBulkAssignTransportista={handleBulkAssignTransportista}
        onBulkPrint={handleBulkPrint}
        onBulkMarkDelivered={handleBulkMarkDelivered}
        isProcessing={isBulkProcessing}
        isPrinting={isPrinting}
      />
      <Card>
        <CardContent className="p-0">
          {currentOrders.length === 0 ? (
            isLoading ? (
              <div className="p-4">
                <TableSkeleton rows={8} columns={8} />
              </div>
            ) : (
              <div className="py-12 text-center text-sm text-muted-foreground">
                No se encontraron pedidos
              </div>
            )
          ) : (
            <OperationsOrdersTable
              orders={currentOrders}
              getClientName={getClientName}
              getSellerInfo={getSellerInfo}
              statusBadge={tabs.find((tab) => tab.key === subTab)?.statusBadge}
              showActions={
                tabs.find((tab) => tab.key === subTab)?.showActions ?? false
              }
              actions={tabs.find((tab) => tab.key === subTab)?.actions}
              onRowDetailClick={handleRowDetailClick}
              selectedOrderIds={selectedOrderIds}
              onSelectOrder={handleSelectOrder}
              isAllSelected={isAllSelected(currentOrders)}
              onSelectAll={() => handleSelectAll(currentOrders)}
              showCheckboxes={subTab !== "delivered"}
              currentTab={subTab}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          )}
        </CardContent>
      </Card>

      <OperationsOrdersPagination
        currentPage={currentPage}
        totalPages={totalPages}
        setCurrentPage={setCurrentPage}
        startIndex={startIndex}
        endIndex={endIndex}
        totalItems={sortedOrders.length}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
      />

      {selectedOrder && (
        <OrderDetailDialog
          selectedOrder={selectedOrder}
          setSelectedOrder={setSelectedOrder}
          getSellerInfo={getSellerInfo}
          getApproverRejecterInfo={getApproverRejecterInfo}
          clientNames={clientNames}
          items={salesRequestItems[selectedOrder.id] || []}
          onOrderStatusChange={fetchSalesRequests}
          clientRiskStatus={clientRiskStatus}
          clientInfo={clientInfo}
        />
      )}

      {guiaDialogOrder && (
        <CreateDispatchGuideDialog
          isOpen={!!guiaDialogOrder}
          onClose={() => setGuiaDialogOrder(null)}
          onConfirm={handleConfirmGuia}
          selectedOrder={guiaDialogOrder}
          isCreating={isCreatingDispatchGuide === guiaDialogOrder.id}
        />
      )}

      <BulkTransportistaAssignmentDialog
        open={transportistaDialogOpen}
        onOpenChange={setTransportistaDialogOpen}
        selectedOrderIds={getSelectedOrders(sortedOrders).map((o) => o.id)}
        onSuccess={() => {
          fetchSalesRequests();
          clearSelection();
        }}
      />

      <DeliveredDateDialog
        open={deliveryDialogOpen}
        onOpenChange={setDeliveryDialogOpen}
        onConfirm={(startDate, endDate) => {
          confirmDelivery(startDate, endDate);
          clearSelection();
        }}
        isLoading={Boolean(isLoadingTransition) || isBulkProcessing}
      />

      <AlertDialog
        open={markDeliveredConfirmOpen}
        onOpenChange={setMarkDeliveredConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Marcar como entregado</AlertDialogTitle>
            <AlertDialogDescription>
              Se marcarán {selectedOrderIds.size} pedido
              {selectedOrderIds.size > 1 ? "s" : ""} como entregado
              {selectedOrderIds.size > 1 ? "s" : ""}. Esta acción los mueve
              directamente a la columna Entregados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBulkProcessing}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmBulkMarkDelivered}
              disabled={isBulkProcessing}
            >
              Marcar como entregado
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
};

export default OperationsOrdersPage;
